import { Component, OnDestroy } from '@angular/core';
import { TestRecorderService } from '../../services/test-recorder/test-recorder.service';
import { RecordingIssue } from '@sc/types';
import { ModalController } from '@ionic/angular';
import { TestEquipmentResultPage } from '../../modals/test-equipment-result/test-equipment-result.page';
import { Subscription } from 'rxjs';
import { Firestore, addDoc, collection } from '@angular/fire/firestore';
import { UserService } from '../../services/user/user.service';
import { StatsService } from '../../services/stats/stats.service';

@Component({
  selector: 'sc-test-equipment',
  templateUrl: './test-equipment.component.html',
  styleUrls: ['./test-equipment.component.scss'],
})
export class TestEquipmentComponent implements OnDestroy {
  testing = false;
  stoppingTest = false;
  progress = 0;
  progressText = '';

  constructor(
    private testRecorderService: TestRecorderService,
    private modalController: ModalController,
    private userService: UserService,
    private firestore: Firestore,
    private statsService: StatsService
  ) {}

  showModal() {
    this.modalController
      .create({
        component: TestEquipmentResultPage,
        cssClass: 'test-equipment-result-modal',
      })
      .then((modal) => modal.present());
    return;
  }
  async startTestEquipment() {
    if (this.testing) {
      return;
    }
    this.reset();
    this.testing = true;

    await this.testRecorderService.startRecording();
    const recordingStartedTime = Date.now();
    this.progressText = 'Recording a 10 second clip. Mic check. 🎤';
    await new Promise<void>((res) => {
      const interval = setInterval(() => {
        const timeElapsed = Date.now() - recordingStartedTime;
        this.progress = (timeElapsed / 10000) * 0.7;
        if (timeElapsed >= 10000 || !this.testing) {
          clearInterval(interval);
          res();
        }
      }, 100);
    });

    // Bail if the user canceled the test manually.
    if (!this.testing) return;
    const { warnings, recordingInfo, update$ } = await this.testRecorderService.stopRecording();

    this.progressText = 'Finalizing the test recording... 🎧';
    this.progress = 0.7;

    const stats = { ...recordingInfo?.stats?.audio, ...recordingInfo?.stats?.video };

    let testRecordingUpdatesSub: Subscription;
    let uploadTimedOut = false;
    await new Promise<void>((res) => {
      testRecordingUpdatesSub = update$.subscribe(() => {
        if (!recordingInfo?.queue?.size || recordingInfo?.queue?.size === 0) {
          res();
        }
      });

      setTimeout(() => {
        uploadTimedOut = true;
        res();
      }, 10000);
    });

    testRecordingUpdatesSub?.unsubscribe();

    // Bail if the user canceled the test manually.
    if (!this.testing) return;
    this.progressText = 'Perfect! Now Checking Connection... 🚀';
    this.progress = 0.8;
    if (stats.bytesTransferred === 0) {
      warnings.push(RecordingIssue.DATA_UPLOAD_FAILED);
      this.progressText = 'Could not upload any data!';
    } else if (uploadTimedOut) {
      warnings.push(RecordingIssue.DATA_UPLOAD_SLOW);
    }

    const testRecordingsCol = collection(
      this.firestore,
      'diagnostics',
      this.userService.activeUser$.value.uid,
      'testRecordings'
    );
    const payload = {
      timestamp: Date.now(),
      recordingInfo: { ...recordingInfo, queue: null },
      warnings: warnings,
      deviceInfo: this.testRecorderService.deviceInfo,
      //firefox is weird and fails if the object is not deep cloned
      connectionStats: JSON.parse(JSON.stringify(this.statsService.connectionStats$.value)),
    };
    await new Promise<void>((res) => {
      const interval = setTimeout(() => {
        res();
        warnings.push(RecordingIssue.FIRESTORE_CONNECTION_FAILED);
      }, 10000);

      addDoc(testRecordingsCol, payload).then(() => {
        clearTimeout(interval);
        res();
      });
    });
    await new Promise((resolve) => setTimeout(resolve, 1000));
    this.progress = 1;
    this.progressText = 'Done! 🎉';
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Bail if the user canceled the test manually.
    if (!this.testing) return;

    const modal = await this.modalController.create({
      component: TestEquipmentResultPage,
      cssClass: 'test-equipment-result-modal',
    });
    await modal.present();

    this.testing = false;
  }

  async forceStopTestEquipment() {
    this.stoppingTest = true;
    try {
      await this.testRecorderService.stopRecording();
    } catch (err) {
      console.error(err);
    }

    this.stoppingTest = false;
    this.reset();
  }

  async reset() {
    this.testing = false;
    this.progress = 0;
    this.progressText = 'Initializing the test recorder... 🎙';
  }

  ngOnDestroy() {
    // So that the test recorder doesn't keep running in the background when the user navigates away.
    this.forceStopTestEquipment();
  }
}
